import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

/**
 * A simple anchored popover/panel — used by ProvenancePopover and
 * FamilyDrawer. Closes on outside click or Escape.
 *
 * Focus management lives here rather than in each consumer, on purpose: any
 * component built on Sheet — today ProvenancePopover and FamilyDrawer,
 * whatever uses it next — gets the same accessible-dialog behavior for free.
 * On open, focus moves into the sheet (so keyboard/screen-reader users land
 * on the new content, not left behind on a trigger that's still on screen
 * but no longer where the action is). On close, focus returns to whatever
 * had it before the sheet opened — but only if focus hasn't already moved
 * somewhere else on its own (e.g. the browser resets it to <body> when the
 * previously-focused trigger or the sheet's own DOM is removed). That guard
 * keeps this from yanking focus away from a genuinely new target, such as a
 * different trigger the user clicked to open a different sheet.
 */
export function Sheet({
  open,
  onClose,
  children,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    ref.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onClickOutside);
      if (document.activeElement === document.body || document.activeElement == null) {
        previouslyFocused.current?.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      tabIndex={-1}
      className={className ?? "absolute z-20 mt-2 w-72 rounded-lg border border-border bg-paper p-4 shadow-lg"}
    >
      {children}
    </div>
  );
}
